import { css } from "@emotion/css"
import * as DG from "ui/dialogs"
import { useI18n } from "providers/I18n"
import Button from "ui/buttons/Button"
import { useState, MouseEvent, ChangeEvent } from "react"
import { TextInput } from "components/inputs/TextInput"
import { useAuthenticated } from "providers/A12n"
import { WithAuthority } from "components/WithAuthority"
import { TargetParams } from "models/targets"
import { IconButton } from "@material-ui/core"
import { ReactComponent as DeleteIcon } from "assets/delete-grey.svg"
const maxValue = 255
const validRange = new RegExp("(^[0-9]+[-]*[0-9]+$)")
export type AddNewVariableModalprops = {
  open: boolean
  onClose: () => void
  data: string[]
  onUpdate: (row: TargetParams, columnKey: string, value: Array<string>) => void
  row: TargetParams
}
export type Option = {
  label: string
  error: boolean
}
export function AddNewVariableModal(props: AddNewVariableModalprops) {
  const { open, onClose, data, onUpdate, row } = props
  const { t } = useI18n()
  const { hasAuthority } = useAuthenticated()
  const [options, setOptions] = useState<Option[]>(
    data.map((i) => ({
      label: i,
      error: false,
    })),
  )

  const isAgeRange = row.semantic === "AGE_RANGE"

  const handleBlur =
    (evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | undefined) =>
    (option: Option) => {
      const { value } = evt?.target ?? {}

      if (isAgeRange) {
        if (!value?.match(validRange) || value === "") {
          option.error = true
        } else {
          const from = Number(value?.split("-")[0]) ?? ""
          const to = Number(value?.split("-")[1]) ?? ""
          if (Number(from) > Number(to)) {
            option.error = true
          } else {
            option.error = false
          }
        }
      }
    }
  const handleChange =
    (evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | undefined) =>
    (option: Option, index: number) => {
      const { value } = evt?.target ?? {}
      const valueLength = value?.length
      setOptions((s) => {
        let _options = [...s]
        if ((valueLength as number) <= maxValue) {
          _options.splice(index, 1, {
            label: value || "",
            error: option.error,
          })
        }

        return _options
      })
    }

  const handleSubmit = () => {
    setOptions(options)
    const pld = options.map((o) => o.label)
    onUpdate(row, "variables", pld)
    onClose()
  }
  const handleCancel = () => {
    setOptions(
      data.map((i) => ({
        label: i,
        error: false,
      })),
    )
    onClose()
  }
  const handleClose = () => {
    if (!options.some((o) => o.error)) {
      setOptions(
        data.map((i) => ({
          label: i,
          error: false,
        })),
      )
      onClose()
    }
  }
  const addRow = () => {
    setOptions((s) => [...s, { label: "", error: false }])
  }
  const deleteRow = (_: MouseEvent<SVGSVGElement>, index: number) => {
    setOptions((s) => {
      let _options = [...s]
      _options.splice(index, 1)
      return _options
    })
  }

  const id = "add-variable-dialog"
  return (
    <DG.Dialog
      fullWidth={false}
      open={open}
      onClose={handleClose}
      id={id}
      className={styles.dialog}
    >
      <DG.DialogHeader>
        <DG.DialogTitle id={id} className="BodyBody-1BlackBold">
          {t("admin.addNewVariable")}
        </DG.DialogTitle>

        <DG.DialogClose onClose={handleClose} />
      </DG.DialogHeader>

      <DG.DialogContent
        center
        paddingTop
        className={
          hasAuthority(["AUTH_ACCESS_MARKET_FINANCE"])
            ? styles.containerWithButtons
            : styles.container
        }
      >
        {options?.length
          ? options.map((o, idx) => (
              <div key={idx} className={styles.inputRow}>
                <TextInput
                  variant="outlined"
                  color="primary"
                  size="medium"
                  margin="normal"
                  value={o.label || ""}
                  onChange={(e) => handleChange(e)(o, idx)}
                  onBlur={(e) => handleBlur(e)(o)}
                  className={styles.input}
                  disabled={!hasAuthority(["AUTH_ACCESS_MARKET_FINANCE"])}
                  multiline
                  placeholder={
                    isAgeRange ? t("admin.field.format.placeholder") : ""
                  }
                  error={!!o.error}
                  errorMessage={
                    !!o.error ? t("admin.field.wrong.format.error") : ""
                  }
                />
                <WithAuthority allowed={["AUTH_ACCESS_MARKET_FINANCE"]}>
                  <IconButton className={styles.iconButton}>
                    <DeleteIcon
                      onClick={(e: MouseEvent<SVGSVGElement>) =>
                        deleteRow(e, idx)
                      }
                      className={styles.deleteIcon}
                    />
                  </IconButton>
                </WithAuthority>
              </div>
            ))
          : null}
        <WithAuthority allowed={["AUTH_ACCESS_MARKET_FINANCE"]}>
          <Button
            onClick={addRow}
            color="primary"
            className={styles.textButton}
          >
            {t("admin.addNewVariable")}
          </Button>
        </WithAuthority>
      </DG.DialogContent>
      {hasAuthority(["AUTH_ACCESS_MARKET_FINANCE"]) ? (
        <DG.DialogFooter>
          <DG.DialogActions>
            <Button variant="outlined" color="primary" onClick={handleCancel}>
              {t("market.addVariable.cancel")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="reset"
              onClick={handleSubmit}
              disabled={
                !hasAuthority(["AUTH_ACCESS_MARKET_FINANCE"]) ||
                options.some((o) => o.error)
              }
            >
              {t("market.addVariable.done")}
            </Button>
          </DG.DialogActions>
        </DG.DialogFooter>
      ) : null}
    </DG.Dialog>
  )
}
const styles = {
  container: css`
    min-width: 467px;
    max-height: 640px;
    overflow: auto;
    margin: 0 var(--spacing-xs) var(--spacing-xs) 0;
  `,
  containerWithButtons: css`
    min-width: 467px;
    max-height: 640px;
    overflow: auto;
    margin: 0 var(--spacing-xs) 0 0;
  `,
  inputRow: css`
    display: flex;
    padding-right: var(--spacing-xxl-7);
    align-items: center;
    justify-content: space-between;

    :first-child {
      > svg {
        display: none;
      }
    }
  `,
  input: css`
    min-width: 280px;
  `,
  textButton: css`
    --btn-padding: 0;
    --btn-min-width: 0;
    --btn-line-height: 14px;
    --btn-color: inherit;
    --btn-font-weight: normal;
    --btn-bg-hover: transparent;
    --btn-bg-active: transparent;
    --btn-border-color-hover: transparent;
    --btn-bg-focus-ring-color: transparent;
    width: fit-content;

    &:focus {
      box-shadow: none;
    }
  `,
  dialog: css`
    *::-webkit-scrollbar {
      width: 6px;
      border-radius: 4px;
      background: var(--primary-light);
    }
    *::-webkit-scrollbar-thumb {
      width: 6px;
      height: var(--spacing-xl);
      border-radius: 4px;
      background: var(--primary-main);
    }
  `,
  iconButton: css`
    &.MuiIconButton-root {
      margin-left: var(--spacing-s);
      margin-top: var(--spacing-xs);
    }
  `,
  deleteIcon: css`
    width: var(--spacing-xl);
    height: var(--spacing-xl);
    cursor: pointer;
    color: var(--grey);
  `,
  errorMsg: css`
    margin: 0 5px;
  `,
}
